import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, Users, Flame, ChevronRight, RefreshCw } from 'lucide-react';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const PARTY_COLORS = {
  labour: '#E4003B',
  conservative: '#0087DC',
  libdem: '#FAA61A',
  snp: '#FDF38E',
  reform: '#12B6CF',
  green: '#6AB023',
};

const TrendingPage = () => {
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrending();
  }, []);

  const fetchTrending = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/politicians/trending`);
      setTrending(response.data.trending || []);
    } catch (error) {
      console.error('Failed to fetch trending:', error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-20" data-testid="trending-page">
      <SEO 
        title="Trending Politicians"
        description="See which UK politicians are making headlines right now"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-red-500 via-orange-500 to-amber-500 py-16 text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur rounded-full mb-6">
            <Flame className="h-4 w-4" />
            <span className="text-sm font-bold">Live Tracking</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-light mb-4">
            Trending Politicians
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            See who's making headlines right now across UK political news
          </p>
        </div>
      </section>

      {/* Trending List */}
      <section className="max-w-4xl mx-auto px-4 -mt-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-6 w-6 text-orange-500" />
              <h2 className="font-bold text-xl">Most Mentioned Today</h2>
            </div>
            <button 
              onClick={fetchTrending}
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500">Loading trending politicians...</p>
            </div>
          ) : trending.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {trending.map((politician, idx) => {
                const partyColor = PARTY_COLORS[politician.party?.toLowerCase()] || '#666';
                const slug = politician.name?.toLowerCase().replace(/ /g, '-').replace(/'/g, '') || '';
                
                return (
                  <Link
                    key={idx}
                    to={`/politician/${slug}`}
                    className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center font-bold text-slate-600 text-lg">
                      {idx + 1}
                    </div>
                    <div 
                      className="w-1 h-12 rounded-full"
                      style={{ backgroundColor: partyColor }}
                    />
                    <div className="flex-1">
                      <div className="font-bold text-lg group-hover:underline">{politician.name}</div>
                      <div className="text-sm text-slate-500">
                        {politician.politician_type || 'MP'} • {politician.party}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-2xl font-bold text-orange-500">{politician.mentions}</div>
                      <div className="text-xs text-slate-400">mentions</div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-slate-600 transition-colors" />
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">No Trending Data Yet</h3>
              <p className="text-slate-500">
                Politicians will appear here as news articles are scraped and analyzed.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrendingPage;
